import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import FilterBar, { FilterOption } from '../../component/FilterBar';
import Loading from '../../component/Loading';
import CardGallery from '../../component/CardGallery';
import { OutflowImageProps, OutflowPreviewMeta } from '../../component/ImagePreview';
import { Empty, Button} from 'antd';
import { PictureOutlined } from '@ant-design/icons';

interface FilterOptions {
  cameras: string[];
}

const TeachModeDetailsPage: React.FC = () => {
  const { outflowId } = useParams<{ outflowId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // States for images and pagination
  const [images, setImages] = useState<OutflowImageProps[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(parseInt(searchParams.get('page') || '1', 10));
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortBy, setSortBy]       = useState<string>(searchParams.get('sort_by') || 'timestamp');
  const [sortOrder, setSortOrder] = useState<'asc'|'desc'>( (searchParams.get('sort_order') as any) || 'desc' );

  // State for filter options (for FilterBar) and its loading state
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    cameras: [],
  });
  const [filterLoading, setFilterLoading] = useState<boolean>(true);

  // Helper to get filter values from URL params
  const getArrayParamFromUrl = (paramName: string): string[] => {
    const params = searchParams.getAll(paramName);
    return params.length > 0 ? params : [];
  };

  // State for filters used in the filter bar
  const [filters, setFilters] = useState({
    part_name: getArrayParamFromUrl('part_name'),
    part_number: getArrayParamFromUrl('part_number'),
    model: getArrayParamFromUrl('model'),
    system_name: getArrayParamFromUrl('system_name'),
    time_range: getArrayParamFromUrl('time_range'),
  });

  // Update URL with current page and filters
  const updateUrlWithFilters = (page: number, filterValues: Record<string, any>) => {
    const newParams = new URLSearchParams();
    newParams.set('page', page.toString());
    Object.entries(filterValues).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          value.forEach(val => newParams.append(key, val));
        }
      } else if (value) {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams);
  };

  // Fetch images (with filters & pagination) for the current outflowId
  const fetchImages = async (page: number, filterValues: Record<string, any>) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.set('page', page.toString());
      queryParams.set('limit', '20');
      Object.entries(filterValues).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            value.forEach(val => queryParams.append(key, val));
          }
        } else if (value) {
          queryParams.append(key, value);
        }
      });
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/teachmode/images/annotated/${outflowId}?${queryParams.toString()}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch images.');
      }
      const data = await response.json();
      setImages(
        data.images.map((img: any) => ({
          id: img.id,
          timestamp: img.timestamp,
          media_id: img.media_id,
          model: img.model,
          part_name: img.part_name,
          part_number: img.part_number,
          review_status: img.review_status,
          camera: img.camera,
          defects: img.defects.map((defect: any) => ({
            ...defect,
            // Hard conversion: x and width are scaled to 4096, y and height to 3072
            x: defect.x,
            y: defect.y,
            w: defect.w,
            h: defect.h,
          })),
          path: img.path,
        }))
      );
      setTotalPages(data.total_pages);
    } catch (error) {
      console.error('Error fetching images:', error);
      setImages([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch filter options for the filter bar
  const fetchFilterOptions = async () => {
    setFilterLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/filter-options`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch filter options');
      }
      const data = await response.json();
      setFilterOptions(data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    } finally {
      setFilterLoading(false);
    }
  };

  // Handler for changes in filters from the FilterBar
  const handleFilterChange = (key: string, value: string | string[]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // When filters are applied, reset to page 1 and refetch images
  const handleApplyFilters = () => {
    setCurrentPage(1);
    updateUrlWithFilters(1, filters);
    fetchImages(1, filters);
  };

  // Initial fetch of images and filter options
  useEffect(() => {
    fetchImages(currentPage, filters);
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Function to navigate to add images, passing along the system name (if available)
  const handleAddImages = () => {
    const system_name = filters.system_name[0] || '';
    navigate(`/teachmode/add-images/${outflowId}?system_name=${system_name}`, { state: { system_name, outflowId } });
  };

  const filterBarOptions: FilterOption[] = [
    { name: "camera", label: "Camera", type: "select", options: filterOptions.cameras },
  ];

  return (
    <div className="gallery-container">
      {images.length > 0 && (
        <div className="add-images-button">
          <Button type="primary" onClick={handleAddImages}>
            Add Images
          </Button>
        </div>
      )}
      <FilterBar
        filters={filters}
        filterOptions={filterBarOptions}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={(newSortBy, newSortOrder) => {
          setSortBy(newSortBy);
          setSortOrder(newSortOrder);
          // fetchImages(currentPage, pageSize, filters);
        }}
        isLoading={filterLoading}
      />
      {loading ? (
        <Loading message="Loading Images..." />
      ) : images.length === 0 ? (
        <div className="empty-state-container">
          <Empty
            image={<PictureOutlined style={{ fontSize: 64, color: '#bfbfbf' }} />}
            imageStyle={{ height: 80 }}
            description={
              <span className="empty-text">No images linked to this outflow</span>
            }
          >
            <Button type="primary" onClick={handleAddImages}>
              Add Images
            </Button>
          </Empty>
        </div>
      ) : (
        <CardGallery
          CardComponent={OutflowPreviewMeta}
          cardClickPath="/image-view"
          data={images}
          onPageChange={(page: number) => {
            setCurrentPage(page);
            updateUrlWithFilters(page, filters);
            fetchImages(page, filters);
          }}
          itemsPerPage={20}
          totalPages={totalPages}
          currentPage={currentPage}
        />
      )}
    </div>
  );
};

export default TeachModeDetailsPage;
